import { useEffect, useState } from "react";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import ChapterHero from "../components/ChapterHero";
import { getLilSaltiesPage } from "../lib/api";
import { safeHtml } from "../lib/safeHtml";

const DEFAULT_PAGE = {
  seo_title: "Welcome to Lil Salties\u2122",
  seo_description:
    "What Are the Lil Salties\u2122? A lovable herd of storybook friends inspired by the real animals at The Salty Moo.",
  hero_eyebrow: "Chapter 3",
  hero_title: "Lil Salties\u2122",
  hero_subtitle: "Where Little Hearts Grow Big Faith.",
  card_subtitle_question: "What Are the Lil Salties\u2122?",
  body_html:
    "<p>The Lil Salties\u2122 are a lovable herd of storybook friends inspired by the real animals at The Salty Moo. Every donkey, Highland cow, and little adventure begins with a true story from the pasture, then blossoms into a tale filled with laughter, friendship, faith, and heart. Their stories remind children that God often teaches life\u2019s biggest lessons through the smallest everyday moments.</p><p>Welcome to the world of the Lil Salties\u2122\u2014where every adventure starts in the pasture and every story points to something greater.</p><p>Because every great story begins with someone worth meeting\u2026 and around here, we call them the Lil Salties\u2122.</p>",
  next_button_label: "Meet the Herd",
};

const EYEBROW_PALETTE = ["#6A2CB2", "#E85DA1"];
const TITLE_PALETTE = ["#6A2CB2", "#E85DA1"];
const SUBTITLE_PALETTE = [
  "#6A2CB2", "#E85DA1", "#D62839", "#4CAF50", "#579DE6", "#C08D4E", "#4B3A2F",
];

/** Split text on whitespace and paint each word cycling through a palette,
 *  preserving the rainbow feel across admin edits. */
function RainbowText({ text, palette, superscriptTm }) {
  if (!text) return null;
  const parts = text.split(/(\s+)/);
  let wordIdx = 0;
  return parts.map((chunk, i) => {
    if (/^\s+$/.test(chunk)) return <span key={i}>{chunk}</span>;
    const color = palette[wordIdx % palette.length];
    wordIdx += 1;
    // Optional: render ™ suffix as a small superscript (matches original title design)
    if (superscriptTm && chunk.endsWith("\u2122")) {
      const base = chunk.slice(0, -1);
      return (
        <span key={i} style={{ color }}>
          {base}
          <sup style={{ fontSize: "0.5em", verticalAlign: "super", color: "#4CAF50" }}>
            &trade;
          </sup>
        </span>
      );
    }
    return (
      <span key={i} style={{ color }}>{chunk}</span>
    );
  });
}

export default function LilSalties() {
  const [page, setPage] = useState(DEFAULT_PAGE);
  useEffect(() => {
    getLilSaltiesPage()
      .then((p) => setPage({ ...DEFAULT_PAGE, ...(p || {}) }))
      .catch(() => {});
  }, []);

  return (
    <div className="parchment min-h-screen" data-testid="lil-salties-page">
      <SEO
        title={page.seo_title}
        description={page.seo_description}
        path="/lil-salties"
      />

      <ChapterHero
        path="/lil-salties"
        image="/assets/heroes/lil-salties.webp"
        eyebrow={<RainbowText text={page.hero_eyebrow} palette={EYEBROW_PALETTE} />}
        title={<RainbowText text={page.hero_title} palette={TITLE_PALETTE} superscriptTm />}
        titleAlt={page.hero_title}
        subtitle={<RainbowText text={page.hero_subtitle} palette={SUBTITLE_PALETTE} />}
        position="top-left"
        contentStyle={{ marginLeft: "18vw" }}
      />

      <div className="max-w-3xl mx-auto px-6 -mt-8 relative z-10 flex flex-col items-center text-center pb-24">
        <h1 className="sr-only" data-testid="lil-salties-title">
          {page.seo_title}
        </h1>

        {/* Welcome copy — parchment card. Every paragraph shares the same
            display-italic size for a unified, storybook feel. */}
        <div
          data-testid="lil-salties-welcome-card"
          className="mt-6 w-full max-w-2xl px-8 py-10 rounded-md parchment-deep border border-[#E7DFD5] page-shadow"
        >
          <img
            src="/assets/lil-salties-logo-hero.webp"
            alt="Lil Salties™"
            data-testid="lil-salties-card-logo"
            className="block mx-auto w-[80%] max-w-md h-auto select-none mb-4"
            draggable="false"
          />
          <p
            className="font-display italic text-xl md:text-2xl text-[#D62839] leading-snug"
            data-testid="lil-salties-subtitle"
          >
            {page.card_subtitle_question}
          </p>
          <div className="gold-rule mx-auto mt-6 mb-6 w-32" />
          <div
            className="space-y-6 font-display italic text-xl md:text-2xl leading-[1.55] text-[#4B3A2F] prose-winks"
            dangerouslySetInnerHTML={safeHtml(page.body_html)}
          />
        </div>
      </div>

      <NextPage to="/meet-the-herd" label={page.next_button_label} />
    </div>
  );
}
